import { Menu, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState, useRef } from "react";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import Button from "../Button";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
// svg served from public/icons
import CustomInput from "../CustomInput";
import { ClinicalServiceTypes } from "../../patient/text/data";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  createNotepad,
  deleteNotepad,
  updateNotepad,
} from "../../../redux/modules/patient/actions";
import { ReactSVG } from "react-svg";
import NewToolTip from "../New-ToolTip";
import { formatDateToDD_MMM_YYYY, compareDates, calculateResizeBounds } from "../../../helpers/functions";

const ICON_BTN_CLS =
  "flex items-center justify-center p-1 transition-all rounded-full size-10 cursor-pointer bg-teal-300 hover:bg-REELTeal text-white";

const notepadTypes = {
  Patient: "patientComments",
  Pharmacist: "pharmacistComments",
};

const saveNoteTooltipContent = (
  <div className="bg-gray-10 text-teal-300 p-3 rounded-md max-w-[280px] text-sm">
    <p className="mb-2 font-semibold text-teal-300 text-[16px] text-center">Save Notes in Notepad</p>
    <ul className="flex flex-col gap-1 list-none">
      <li className="flex gap-1"><span className="mt-0.5 shrink-0">•</span><span>Selecting <span className="font-semibold">"Save Notes in Notepad"</span> stores the note permanently in the Notepad until it is manually deleted.</span></li>
      <li className="flex gap-1"><span className="mt-0.5 shrink-0">•</span><span>All notes are saved to the Patient Chart → Medical Summary, regardless of whether a clinical service is active.</span></li>
    </ul>
  </div>
);

const scratchNoteTooltipContent = (
  <div className="bg-gray-10 text-teal-300 p-3 rounded-md max-w-[280px] text-sm">
    <p className="mb-2 font-semibold text-teal-300 text-[16px] text-center">Temporary / Scrap Note</p>
    <ul className="flex flex-col gap-1 list-none">
      <li className="flex gap-1"><span className="mt-0.5 shrink-0">•</span><span>Selecting <span className="font-semibold">"Temporary / Scrap Note"</span> deletes the note automatically at the end of the service or when the patient chart is closed.</span></li>
    </ul>
  </div>
);

const NotePadModal = ({
  open = false,
  handleClose = () => {},
  confirmAction = () => {},
  onCross = () => {},
  serviceName = "",
  patientChartTabName = "",
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { id: patientId, serviceId } = useParams();
  const { notepad } = useSelector((state) => state.patients);
  const { clinicalServicesSmall } = useSelector((state) => state.clinicalService);
  
  const contentRef = useRef(null);
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);
  const hasSetInitialPositionRef = useRef(false);
  const initialNoteRef = useRef({ title: null, comment: null, keepNote: false, noteOption: null });
  
  const [dimensions, setDimensions] = useState({ width: 660, height: 580 });
  const [maxConstraints, setMaxConstraints] = useState([800, 600]);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [notepadType, setNotepadType] = useState("pharmacistComments");
  const [noteTitle, setNoteTitle] = useState(null);
  const [noteComment, setNoteComment] = useState(null);
  const [keepNote, setKeepNote] = useState(false);
  const [noteOption, setNoteOption] = useState(null); // null | "save" | "scratch"
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteList, setNoteList] = useState([]);
  const [showPreviousNotes, setShowPreviousNotes] = useState(true);
  const [showPreviousMANotes, setShowPreviousMANotes] = useState(true);
  const [showPreviousAMRNotes, setShowPreviousAMRNotes] = useState(true);
  const [expandedDates, setExpandedDates] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const maxCharacterLimit = 500;
  const baseDimensions = { width: 660, height: 580 };
  const scaleRaw = Math.min(
    dimensions.width / baseDimensions.width,
    dimensions.height / baseDimensions.height
  );
  const isScaled =
    scaleRaw < 1 &&
    dimensions.width < baseDimensions.width &&
    dimensions.height < baseDimensions.height;
  const scale = isScaled ? scaleRaw : 1;
  const isModalResized =
    dimensions.width !== baseDimensions.width ||
    dimensions.height !== baseDimensions.height;

  const getServiceDisplayName = (code) => {
    if (!code) return "";
    return code
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const getPatientChartTabName = () => {
    if (patientChartTabName) return patientChartTabName;
    
    const path = location.pathname;
    if (path.includes('/clinical-details')) return 'Clinical Details';
    if (path.includes('/medications')) return 'Medications';
    if (path.includes('/vaccines')) return 'Vaccines';
    if (path.includes('/interventions')) return 'Interventions';
    if (path.includes('/medical-summary')) return 'Medical Summary';
    if (path.includes('/charting')) return 'Charting';
    if (path === `/patient/${patientId}/` || path === `/patient/${patientId}`) return 'Patient Details';
    return '';
  };

  const isValidDate = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) && date.getUTCFullYear() > 1900;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString || !isValidDate(dateString)) return "Unknown Date";
    const formatted = formatDateToDD_MMM_YYYY(dateString);
    // Convert "DD-MMM-YYYY" to "DD-MMM YYYY" and uppercase month
    return formatted.replace(/-(\d{4})$/, " $1").toUpperCase();
  };

  const getNoteDate = (note) => {
    if (!note) return null;
    
    // Prioritize systemPeriod (for past notes), ignore default "0001-01-01"
    const systemPeriod = note.systemPeriod || note.SystemPeriod;
    if (systemPeriod) {
      const dateValue = typeof systemPeriod === 'string' 
        ? systemPeriod 
        : systemPeriod.lowerBound || systemPeriod.LowerBound;
      if (dateValue && !dateValue.startsWith('0001-01-01') && isValidDate(dateValue)) {
        return dateValue;
      }
    }
    
    // Fallback to createdAt (normalized by reducer)
    if (note.createdAt && isValidDate(note.createdAt)) {
      return note.createdAt;
    }
    
    return new Date().toISOString();
  };


  const groupNotesByDate = (notes) => {
    const sortedNotes = [...notes].sort((a, b) => {
      const dateA = getNoteDate(a);
      const dateB = getNoteDate(b);
      return compareDates(dateB, dateA);
    });

    const grouped = {};
    sortedNotes.forEach(note => {
      const noteDate = getNoteDate(note);
      if (!noteDate) return;
      
      const dateKey = formatDateForDisplay(noteDate);
      if (!grouped[dateKey]) {
        grouped[dateKey] = {};
      }
      
      // Group by section name - always use default section names, not user-entered title
      const sectionKey = note.notePadClass === "patient" 
        ? "Patient Chart" 
        : note.notePadClass === "minorAilment" 
        ? "Minor Ailments - Allergic Rhinitis" 
        : "Annual Medication Review";
      
      if (!grouped[dateKey][sectionKey]) {
        grouped[dateKey][sectionKey] = [];
      }
      grouped[dateKey][sectionKey].push(note);
    });
    return grouped;
  };


  const renderNotesForSection = (notes, sectionKey, dateKey) => {
    if (notes.length === 0) return null;
    
    const sectionId = `${dateKey}-${sectionKey}`;
    const isExpanded = expandedSections[sectionId] !== false;

    return (
      <div key={sectionId} className="flex flex-col gap-1 ml-2">
        <div 
          className="flex items-center justify-between cursor-pointer py-1 border-b border-gray-100"
          onClick={() => setExpandedSections(prev => ({ ...prev, [sectionId]: !isExpanded }))}
        >
          <div className="flex items-center gap-2">
            {notes.some((n) => n.keepNote) && (
              <div className="flex items-center justify-center w-4 h-4 rounded-full bg-REELTeal">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={2} />
              </div>
            )}
            <span className="text-base font-semibold text-teal-300">{sectionKey}</span>
          </div>
          <div className="w-10 flex items-center justify-end mr-2">
            {isExpanded ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-600" />
            )}
          </div>
        </div>
        {isExpanded && (
          <div className="flex flex-col gap-1">
            {notes.map((note, noteIndex) => (
              <div
                key={note.id || noteIndex}
                className="pl-2 pr-1 py-1 bg-gray-20 rounded-md flex items-center justify-between"
              >
                <div className="flex-1 overflow-x-hidden">
                  {note.title && 
                   typeof note.title === 'string' && 
                   note.title.trim().length > 0 && 
                   note.title.trim() !== sectionKey && (
                    <span className="whitespace-pre-wrap text-sm break-words text-gray-800 font-medium">
                      {note.title.trim()} -{" "}
                    </span>
                  )}
                  {note.comments && (
                    <span className="whitespace-pre-wrap text-sm break-words text-gray-800">
                      {note.comments}
                    </span>
                  )}
                </div>
              <div className="flex items-center justify-end mt-1 w-10">
                <Menu as="div" className="relative inline-block text-left">
                  <>
                    <div>
                      <Menu.Button
                        onClick={(e) => {
                          const noteItem = e.currentTarget.closest('.bg-gray-20');
                          const dialogBody = e.currentTarget.closest('.dialog-body');
                          
                          if (noteItem && dialogBody) {
                            setTimeout(() => {
                              const currentScrollTop = dialogBody.scrollTop;
                              const noteRect = noteItem.getBoundingClientRect();
                              const bodyRect = dialogBody.getBoundingClientRect();
                              const noteTopInBody = noteRect.top - bodyRect.top + currentScrollTop;
                              const menuHeight = 200;
                              const padding = 20;
                              const targetScroll = noteTopInBody + menuHeight + padding - dialogBody.clientHeight;
                              const spaceBelow = bodyRect.bottom - noteRect.bottom;
                              
                              if (spaceBelow < menuHeight) {
                                dialogBody.scrollTo({
                                  top: Math.max(0, Math.min(targetScroll, dialogBody.scrollHeight - dialogBody.clientHeight)),
                                  behavior: 'smooth'
                                });
                              }
                            }, 150);
                          }
                        }}
                      >
                        <ReactSVG
                          src="/icons/vertical-dots-active.svg"
                          className="flex justify-end items-end w-5"
                        />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform translate-y-2 opacity-0 scale-95"
                      enterTo="transform translate-y-0 opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform translate-y-0 opacity-100 scale-100"
                      leaveTo="transform translate-y-2 opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 min-w-[140px] w-auto origin-top bg-white divide-y divide-gray-50 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 top-full mt-1 z-50">
                        <Menu.Item>
                          <button
                            className="block w-full text-left px-2 py-2 hover:bg-gray-50"
                            onClick={() => {
                              setSelectedNote(note);
                            }}
                          >
                            Edit
                          </button>
                        </Menu.Item>
                        <Menu.Item>
                          <button
                            className="block w-full text-left px-2 py-2 hover:bg-gray-50"
                            onClick={() => {
                              onDelete(note);
                            }}
                          >
                            Delete
                          </button>
                        </Menu.Item>
                        <Menu.Item>
                          <button
                            className="block w-full text-left px-2 py-2 hover:bg-gray-50 whitespace-nowrap"
                            onClick={() => {
                              onCopy(note);
                            }}
                          >
                            Copy to{" "}
                            {note.notePadType === notepadTypes.Patient
                              ? "Pharmacist"
                              : "Patient"}
                          </button>
                        </Menu.Item>
                        <Menu.Item>
                          <button
                            className="block w-full text-left px-2 py-2 hover:bg-gray-50 whitespace-nowrap"
                            onClick={() => {
                              onMove(note);
                            }}
                          >
                            Move to{" "}
                            {note.notePadType === notepadTypes.Patient
                              ? "Pharmacist"
                              : "Patient"}
                          </button>
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </>
                </Menu>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const getServiceIds = () => {
    const medicationReviewId = 
      serviceName === ClinicalServiceTypes.MedicationReview && serviceId !== undefined
        ? serviceId
        : null;
    const minorAilmentId = 
      serviceName === ClinicalServiceTypes.AllergicRhinitis && serviceId !== undefined
        ? serviceId
        : null;
    const ptId = serviceId === undefined ? patientId : null;
    return { medicationReviewId, minorAilmentId, ptId };
  };

  useEffect(() => {
    if (notepad.length > 0) {
      const filteredByType = notepad.filter((x) => x.notePadType === notepadType);
      setNoteList(filteredByType);
    } else {
      setNoteList([]);
    }
  }, [notepad, notepadType]);

  useEffect(() => {
    if (!open) return;

    if (selectedNote !== null) {
      const resolvedOption = selectedNote.scratchNote ? "scratch" : selectedNote.keepNote ? "save" : null;
      initialNoteRef.current = {
        title: selectedNote.title ?? "",
        comment: selectedNote.comments ?? "",
        keepNote: !!selectedNote.keepNote,
        noteOption: resolvedOption,
      };
      setNoteTitle(initialNoteRef.current.title);
      setNoteComment(initialNoteRef.current.comment);
      setKeepNote(!!initialNoteRef.current.keepNote);
      setNoteOption(resolvedOption);
    } else {
      initialNoteRef.current = { title: "", comment: "", keepNote: false, noteOption: null };
      setNoteTitle("");
      setNoteComment("");
      setKeepNote(false);
      setNoteOption(null);
    }
  }, [open, selectedNote]);

  const calculateInitialPosition = () => {
    const originalWidth = 660;
    const originalHeight = 580;
    const iconOffsetFromRight = 40;
    const iconOffsetFromTop = 250;
    const iconHeight = 40;
    const gapBelowIcon = 10;
    const margin = 20;
    const headerHeight = 64;
    const topMargin = margin + headerHeight;
    
    const modalRightEdge = window.innerWidth - iconOffsetFromRight;
    const modalLeftEdge = modalRightEdge - originalWidth;
    const modalTopEdge = iconOffsetFromTop + iconHeight + gapBelowIcon;
    
    const modalCenterX = modalLeftEdge + (originalWidth / 2);
    const modalCenterY = modalTopEdge + (originalHeight / 2);
    
    let initialX = modalCenterX - (window.innerWidth / 2);
    let initialY = modalCenterY - (window.innerHeight / 2);
    
    const halfWidth = originalWidth / 2;
    const halfHeight = originalHeight / 2;
    const maxX = window.innerWidth / 2 - halfWidth - margin;
    const maxY = window.innerHeight / 2 - halfHeight - margin;
    const minX = -window.innerWidth / 2 + halfWidth + margin;
    const minY = -window.innerHeight / 2 + halfHeight + topMargin;
    
    initialX = Math.max(minX, Math.min(maxX, initialX));
    initialY = Math.max(minY, Math.min(maxY, initialY));
    
    return { x: initialX, y: initialY };
  };

  // Set initial position when modal opens
  useEffect(() => {
    if (open && !hasSetInitialPositionRef.current) {
      const initialPos = calculateInitialPosition();
      setPosition(initialPos);
      hasSetInitialPositionRef.current = true;
    } else if (!open) {
      // Delay reset until after transition completes to prevent flicker
      const timeoutId = setTimeout(() => {
        hasSetInitialPositionRef.current = false;
        setPosition({ x: 0, y: 0 });
        setDimensions({ width: 660, height: 580 });
      }, 300); // Match transition duration
      
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  useEffect(() => {
    const updateConstraints = () => {
      const margin = 20;
      const headerHeight = 64;
      const topMargin = margin + headerHeight;
      const maxWidth = window.innerWidth - 40;
      const maxHeight = window.innerHeight - topMargin - margin;
      setMaxConstraints([maxWidth, maxHeight]);
    };

    updateConstraints();
    window.addEventListener("resize", updateConstraints);
    return () => window.removeEventListener("resize", updateConstraints);
  }, []);

  useEffect(() => {
    if (selectedNote !== null) {
      setNoteTitle(selectedNote.title);
      setNoteComment(selectedNote.comments);
      setNotepadType(selectedNote.notePadType);
      setKeepNote(selectedNote.keepNote || false);
      setNoteOption(selectedNote.scratchNote ? "scratch" : selectedNote.keepNote ? "save" : null);
    } else {
      setKeepNote(false);
      setNoteOption(null);
    }
  }, [selectedNote]);

  const adjustTextareaHeight = (textarea) => {
    if (textarea) {
      const maxHeight = 460;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  };

  useEffect(() => {
    const textarea = document.querySelector('textarea[name="Comment"]');
    adjustTextareaHeight(textarea);
  }, [noteComment]);

  const onClose = () => {
    if (selectedNote !== null) {
      setSelectedNote(null);
    } else {
      onCross();
    }
    resetForm();
  };

  const handleOverlayClick = (e) => {
    if (isDraggingRef.current || isResizingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.target === e.currentTarget) {
      onCross();
      resetForm();
    }
  };

  const resetForm = () => {
    setNoteComment("");
    setNoteTitle("");
    setKeepNote(false);
    setNoteOption(null);
    setSelectedNote(null);
  };

  // Helper function to get actual service name
  const getActualServiceName = () => {
    if (!serviceId || !clinicalServicesSmall) return "";
    
    const minorAilment = clinicalServicesSmall.minorAilments?.find(ma => ma.id === serviceId);
    if (minorAilment?.minorAilmentTypeCode) {
      return getServiceDisplayName(minorAilment.minorAilmentTypeCode);
    }
    
    const medReview = clinicalServicesSmall.medicationReviews?.find(mr => mr.id === serviceId);
    if (medReview?.medicationReviewTypeCode) {
      return getServiceDisplayName(medReview.medicationReviewTypeCode);
    }
    
    return "";
  };

  // Helper function to get contextual default title
  const getContextualDefaultTitle = () => {
    const isPatientChartContext = serviceId === undefined;
    const detectedTabName = getPatientChartTabName();
    const actualServiceName = getActualServiceName();
    
    if (isPatientChartContext) {
      return `Patient Chart${detectedTabName ? ` - ${detectedTabName}` : ""}`;
    }
    
    if (serviceName === ClinicalServiceTypes.AllergicRhinitis) {
      return `Minor Ailment${actualServiceName ? ` - ${actualServiceName}` : " - Allergic Rhinitis"}`;
    }
    
    if (serviceName === ClinicalServiceTypes.MedicationReview) {
      return actualServiceName || "Annual Medication Review";
    }
    
    return "";
  };

  const onSave = () => {
    // Prevent saving empty comments
    if (!noteComment || (typeof noteComment === 'string' && noteComment.trim().length === 0)) {
      return;
    }
    if ((noteComment || "").length > maxCharacterLimit) {
      return;
    }
    const { medicationReviewId, minorAilmentId, ptId } = getServiceIds();
    const resolvedTitle = selectedNote
      ? (noteTitle && noteTitle.trim().length > 0 ? noteTitle.trim() : (selectedNote.title || ""))
      : (noteTitle && noteTitle.trim().length > 0 ? noteTitle.trim() : "");

    const baseObject = {
      title: resolvedTitle,
      comments: noteComment,
      keepNote: noteOption === "save",
      scratchNote: noteOption === "scratch",
    };

    if (selectedNote === null) {
      const updatedObject = {
        ...baseObject,
        notePadType: notepadType,
        createdAt: new Date().toISOString(),
      };
      dispatch(createNotepad(medicationReviewId, minorAilmentId, ptId, updatedObject));
    } else {
      const updatedObject = {
        ...baseObject,
        notePadType: selectedNote.notePadType,
        createdAt: selectedNote.createdAt || getNoteDate(selectedNote) || new Date().toISOString(),
      };
      dispatch(updateNotepad(selectedNote.id, updatedObject));
    }
    resetForm();
  };

  const onDelete = (note) => {
    dispatch(deleteNotepad(note.id));
    resetForm();
  };

  const onCopy = (note) => {
    const { medicationReviewId, minorAilmentId, ptId } = getServiceIds();
    const createObject = {
      title: note.title,
      comments: note.comments,
      notePadType: note.notePadType === notepadTypes.Patient
        ? notepadTypes.Pharmacist
        : notepadTypes.Patient,
      keepNote: note.keepNote || false,
      scratchNote: note.scratchNote || false,
    };
    dispatch(createNotepad(medicationReviewId, minorAilmentId, ptId, createObject));
  };

  const onMove = (note) => {
    const updatedObject = {
      title: note.title,
      comments: note.comments,
      notePadType: note.notePadType === notepadTypes.Patient
        ? notepadTypes.Pharmacist
        : notepadTypes.Patient,
      keepNote: note.keepNote || false,
      scratchNote: note.scratchNote || false,
    };
    dispatch(updateNotepad(note.id, updatedObject));
  };

  const handleRevert = () => {
    setDimensions(baseDimensions);
    setPosition(calculateInitialPosition());
  };

  const handleModalClose = () => {
    onSave();
    onCross();
    resetForm();
  };

  const renderGroupedNotes = () => {
    if (!noteList.length) return null;

    const groupedByDate = groupNotesByDate(noteList);
    const dateKeys = Object.keys(groupedByDate);

    return (
      <div className="flex flex-col gap-4">
        {dateKeys.map((dateKey) => {
          const dateGroup = groupedByDate[dateKey];
          const isDateExpanded = expandedDates[dateKey] !== false;
          const allNotesInDate = Object.values(dateGroup).flat();
          const hasKeepNote = allNotesInDate.some((note) => note.keepNote);

          return (
            <div key={dateKey} className="flex flex-col gap-1">
              {/* Date Header with Radio Button */}
              <div className="flex items-center gap-2 py-1 px-2 bg-gray-50 rounded">
                {hasKeepNote && (
                  <div className="flex items-center justify-center w-4 h-4 rounded-full bg-REELTeal">
                    <CheckIcon className="w-3 h-3 text-white" strokeWidth={2} />
                  </div>
                )}
                <button
                  type="button"
                  className="flex items-center gap-2 cursor-pointer flex-1 text-left"
                  onClick={() =>
                    setExpandedDates((prev) => ({
                      ...prev,
                      [dateKey]: !isDateExpanded,
                    }))
                  }
                >
                  <span className="text-base font-semibold text-gray-800 flex-1">
                    {dateKey}
                  </span>
                </button>
                <div
                  className="w-10 flex items-center justify-end cursor-pointer"
                  onClick={() => setExpandedDates((prev) => ({ ...prev, [dateKey]: !isDateExpanded }))}
                >
                  {isDateExpanded ? (
                    <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                  )}
                </div>
              </div>

              {/* Sections grouped under this date by title */}
              {isDateExpanded && (
                <div className="flex flex-col gap-1">
                  {Object.keys(dateGroup)
                    .sort()
                    .map((sectionTitle) => {
                      const notesInSection = dateGroup[sectionTitle];
                      return renderNotesForSection(
                        notesInSection,
                        sectionTitle,
                        dateKey
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Transition
      show={open}
      as={Fragment}
      enter="transition-opacity duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        className="fixed inset-0 flex items-center justify-center z-40 pointer-events-none"
        onClick={handleOverlayClick}
        onMouseDown={(e) => {
          // Keep notepad interactive while allowing background interactions.
          if (e.target !== e.currentTarget) {
            e.stopPropagation();
          }
        }}
      >
        <div
          className="pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Draggable
            handle=".dialog-header"
            bounds={(parentNode) => {
              const margin = 20;
              const headerHeight = 64;
              const topMargin = margin + headerHeight;
              const halfWidth = dimensions.width / 2;
              const halfHeight = dimensions.height / 2;
              const parentRect = parentNode.getBoundingClientRect();
              const minY = topMargin - window.innerHeight / 2 + halfHeight;
              
              return {
                left: -parentRect.width / 2 + halfWidth + margin,
                top: minY,
                right: parentRect.width / 2 - halfWidth - margin,
                bottom: parentRect.height / 2 - halfHeight - margin,
              };
            }}
            position={position}
            onStart={() => {
              isDraggingRef.current = true;
            }}
            onDrag={(e, data) => {
              const margin = 20;
              const headerHeight = 64;
              const topMargin = margin + headerHeight;
              const halfHeight = dimensions.height / 2;
              const minY = topMargin - window.innerHeight / 2 + halfHeight;
              const maxY = window.innerHeight / 2 - halfHeight - margin;
              
              const constrainedY = Math.max(minY, Math.min(maxY, data.y));
              setPosition({ x: data.x, y: constrainedY });
            }}
            onStop={(e, data) => {
              setPosition({ x: data.x, y: data.y });
              setTimeout(() => {
                isDraggingRef.current = false;
              }, 100);
            }}
          >
            <ResizableBox
              width={dimensions.width}
              height={dimensions.height}
              minConstraints={[340, 280]}
              maxConstraints={maxConstraints}
              onResizeStart={() => {
                isResizingRef.current = true;
              }}
              onResize={(e, { size }) => {
                const margin = 20;
                const headerHeight = 64;
                const topMargin = margin + headerHeight;
                
                const { constrainedWidth, constrainedHeight, newPosition } = calculateResizeBounds({
                  sizeWidth: size.width,
                  sizeHeight: size.height,
                  maxConstraints,
                  currentPosition: position,
                  margin,
                  topMargin,
                });

                setDimensions({ width: constrainedWidth, height: constrainedHeight });

                if (newPosition.x !== position.x || newPosition.y !== position.y) {
                  setPosition(newPosition);
                }
              }}
              onResizeStop={() => {
                // Small delay to prevent overlay click from triggering
                setTimeout(() => {
                  isResizingRef.current = false;
                }, 100);
              }}
              draggableOpts={{ enableUserSelectHack: false }}
            >
              <div
                ref={contentRef}
                className="bg-white pointer-events-auto w-full h-full border border-gray-300 rounded shadow-md relative"
              >
                <div className="w-full h-full overflow-hidden">
                  <div
                    className="flex flex-col justify-between w-full h-full origin-top-left"
                    style={{
                      transform: isScaled ? `scale(${scale})` : "none",
                      transformOrigin: "top left",
                      width: isScaled ? `${100 / scale}%` : "100%",
                      height: isScaled ? `${100 / scale}%` : "100%",
                    }}
                  >
                      <div className="dialog-header mb-0 bg-teal-25 flex flex-row justify-center px-6 py-5 rounded-b-none rounded-[4px] min-h-[78px] relative flex-shrink-0 border-b border-gray-200">
                        <h3 className="text-center text-2xl font-bold text-teal-300">Notepad</h3>
                        <div className="flex items-center justify-center absolute right-4 gap-3">
                          {isModalResized && (
                            <>
                              <button
                                id="revertSizeHeaderInfoIcon"
                                type="button"
                                className="group p-[6px] transition-all rounded-full max-w-8 cursor-pointer bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                                onClick={handleRevert}
                                aria-label="Revert notepad to original size"
                              >
                                <img
                                  src="/icons/revert-icon.svg"
                                  alt=""
                                  className="!h-6 !w-6 m-auto shrink-0 transition-all group-hover:brightness-0 group-hover:invert"
                                  aria-hidden="true"
                                />
                              </button>
                              <NewToolTip
                                id="revertSizeHeaderTooltip"
                                anchorSelect="#revertSizeHeaderInfoIcon"
                                render={() => (
                                  <div className="bg-gray-10 text-gray-800 p-2 rounded-md text-xs">Revert to original size.</div>
                                )}
                                place="bottom"
                              />
                            </>
                          )}

                          <button
                            type="button"
                            className="p-[6px] transition-all rounded-full max-w-8 cursor-pointer bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                            onClick={handleModalClose}
                          >
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="w-5 h-5 m-auto" strokeWidth="2" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      <div className="px-9 pb-5 gap-5 flex flex-col flex-1 overflow-y-auto text-sm dialog-body">
                        <div className="flex flex-col gap-5 flex-shrink-0">
                          <div className="grid w-full min-w-0 grid-cols-2 gap-4 mt-4">
                            <Button
                              className={`!border-[1px] !h-[52px] w-full min-w-0 !px-4 !py-0 justify-center [&>span]:!overflow-visible [&>span]:!text-clip [&>span]:!whitespace-nowrap [&>span]:text-center ${
                                notepadType === notepadTypes.Pharmacist
                                  ? "!bg-blue-100 !text-teal-300 !border-teal-300"
                                  : "!bg-white !text-gray-800 !border-gray-400"
                              }`}
                              type={
                                notepadType === notepadTypes.Pharmacist
                                  ? "primary"
                                  : "primary-outline"
                              }
                              text={"Pharmacist Comments"}
                              onClick={() => {
                                setNotepadType(notepadTypes.Pharmacist);
                              }}
                            />
                            <Button
                              className={`!border-[1px] !h-[52px] w-full min-w-0 !px-4 !py-0 justify-center [&>span]:!overflow-visible [&>span]:!text-clip [&>span]:!whitespace-nowrap [&>span]:text-center ${
                                notepadType === notepadTypes.Patient
                                  ? "!bg-blue-100 !text-teal-300 !border-teal-300"
                                  : "!bg-white !text-gray-800 !border-gray-400"
                              }`}
                              type={
                                notepadType === notepadTypes.Patient
                                  ? "primary"
                                  : "primary-outline"
                              }
                              text={"Patient Comments"}
                              onClick={() => {
                                setNotepadType(notepadTypes.Patient);
                              }}
                            />
                          </div>
                          <div className="font-normal text-xs flex flex-col gap-3">
                            {/* Single container for Title and Comments */}
                            <div className="relative border border-gray-200 rounded-md bg-white">
                              {/* Title Section */}
                              <div className="bg-gray-10 px-2 py-0 border-b border-gray-200">
                                <label className="inline-flex items-center text-sm font-medium text-teal-300">
                                  Title (optional):{" "}
                                  <input
                                    name="Title"
                                    type="text"
                                    value={noteTitle ?? ""}
                                    onChange={(e) => setNoteTitle(e.target.value || "")}
                                    className="ml-0 flex-1 bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:ring-transparent focus:border-none focus:border-transparent text-gray-800 placeholder-gray-400 shadow-none"
                                  />
                                </label>
                              </div>
                              
                              {/* Comments Section */}
                              <div className="relative p-0">
                                <CustomInput
                                  name="Comment"
                                  className="col-span-12"
                                  inputClassName={`!min-h-[220px] !pb-10 !max-h-[460px] resize-none !overflow-y-hidden focus:outline-none focus:ring-0 focus:ring-transparent ${
                                    (noteComment || "").length > maxCharacterLimit
                                  }`}
                                  label=""
                                  value={noteComment ?? ""}
                                  onChange={(e) => {
                                    const val = e.target.value || "";
                                    setNoteComment(val);
                                    adjustTextareaHeight(e.target);
                                  }}
                                  type="textarea"
                                />

                                {/* Note options — absolutely positioned inside textarea */}
                                <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-4">
                                  {/* Save Notes in Notepad */}
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      id="save-note-option"
                                      name="note-option"
                                      type="radio"
                                      value="save"
                                      checked={noteOption === "save"}
                                      onChange={(e) => setNoteOption(e.target.value)}
                                      onClick={() => {
                                        if (noteOption === "save") setNoteOption(null);
                                      }}
                                      className="w-4 h-4 cursor-pointer text-teal-50 focus:ring-transparent"
                                    />
                                    <label
                                      htmlFor="save-note-option"
                                      className={`text-sm font-semibold cursor-pointer ${
                                        noteOption === "save" ? "text-teal-300" : "text-gray-800"
                                      }`}
                                    >
                                      Save Notes in Notepad
                                    </label>
                                    <ReactSVG
                                      src="/icons/information-dark.svg"
                                      className="w-4 h-4 shrink-0 cursor-pointer"
                                      id="saveNoteInfoIcon"
                                    />
                                  </div>
                                  <NewToolTip
                                    id="saveNoteTooltip"
                                    anchorSelect="#saveNoteInfoIcon"
                                    render={() => saveNoteTooltipContent}
                                    place="top"
                                    clickable={true}
                                  />

                                  {/* Temporary / Scrap Note */}
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      id="scratch-note-option"
                                      name="note-option"
                                      type="radio"
                                      value="scratch"
                                      checked={noteOption === "scratch"}
                                      onChange={(e) => setNoteOption(e.target.value)}
                                      onClick={() => {
                                        if (noteOption === "scratch") setNoteOption(null);
                                      }}
                                      className="w-4 h-4 cursor-pointer text-teal-50 focus:ring-transparent"
                                    />
                                    <label
                                      htmlFor="scratch-note-option"
                                      className={`text-sm font-semibold cursor-pointer ${
                                        noteOption === "scratch" ? "text-teal-300" : "text-gray-800"
                                      }`}
                                    >
                                      Temporary / Scrap Note
                                    </label>
                                    <ReactSVG
                                      src="/icons/information-dark.svg"
                                      className="w-4 h-4 shrink-0 cursor-pointer"
                                      id="scratchNoteInfoIcon"
                                    />
                                  </div>
                                  <NewToolTip
                                    id="scratchNoteTooltip"
                                    anchorSelect="#scratchNoteInfoIcon"
                                    render={() => scratchNoteTooltipContent}
                                    place="top"
                                    clickable={true}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="-mt-1 flex items-center justify-between">
                              <span className="text-teal-300 text-[12px]">Limit: 500 Characters</span>
                              {(noteComment || "").length > maxCharacterLimit && (
                                <span className="text-red-500 text-[12px] font-medium">
                                  Character limit exceeded ({(noteComment || "").length} / {maxCharacterLimit})
                                </span>
                              )}
                            </div>
                          </div>

                          <div className={`grid gap-4 grid-cols-2 -mb-2 pb-2`}>
                            <Button
                              className="min-w-fit !h-[56px]"
                              type={"primary-outline"}
                              text={"Cancel"}
                              onClick={() => {
                                const init = initialNoteRef.current || { title: "", comment: "", keepNote: false, noteOption: null };
                                setNoteTitle(init.title ?? "");
                                setNoteComment(init.comment ?? "");
                                setKeepNote(!!init.keepNote);
                                setNoteOption(init.noteOption ?? null);
                                setSelectedNote(null);
                              }}
                            />
                            <Button
                              className="min-w-fit !h-[56px]"
                              type={"primary"}
                              text={selectedNote ? "Update" : "Save"}
                              onClick={() => {
                                onSave();
                              }}
                              disabled={
                                !noteComment ||
                                (typeof noteComment === 'string' && noteComment.trim().length === 0) ||
                                (noteComment || "").length > maxCharacterLimit
                              }
                            />
                          </div>
                        </div>
                        {noteList.length > 0 && (
                          <div className="pr-1 flex-shrink-0">
                            {renderGroupedNotes()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
          </ResizableBox>
        </Draggable>
      </div>
    </div>
    </Transition>
  );
};

export default NotePadModal;

